import { Box, CircularProgress } from '@mui/material';
import { ArrowLeft, ArrowSquareOut, Check, Plus, X } from '@phosphor-icons/react';
import { Text } from '@telegram-apps/telegram-ui';
import { motion } from 'framer-motion';
import React from 'react';
import { getButtonThemeColors } from '../helpers/theme';
import {
    LIQUID_GLASS_CIRCLE_PRESS_SCALE,
    LIQUID_GLASS_PRESS_ORIGIN,
    LIQUID_GLASS_PRESS_SCALE,
    MINI_BUTTON_ACTION_ICON_SIZE,
    MINI_BUTTON_ACTION_SIZE,
    liquidGlassCircleShadowSx,
    liquidGlassPillShadowSx,
    liquidGlassPressTransition,
    liquidGlassSurfaceFillSx,
} from '../helpers/liquidGlassSurface';
import { useLiquidGlassPress } from '../helpers/useLiquidGlassPress';

function mainNavKind(text: string): 'back' | 'close' | null {
    const normalized = text.trim().toLowerCase();
    if (normalized === 'close') return 'close';
    if (normalized === 'back') return 'back';
    return null;
}

function mainButtonIcon(text: string) {
    const normalized = text.trim().toLowerCase();
    if (normalized.startsWith('new ') || normalized.startsWith('add ')) return <Plus size={MINI_BUTTON_ACTION_ICON_SIZE} weight="bold" />;
    if (normalized.startsWith('open ')) return <ArrowSquareOut size={MINI_BUTTON_ACTION_ICON_SIZE} weight="bold" />;
    return <Check size={MINI_BUTTON_ACTION_ICON_SIZE} weight="bold" />;
}

export interface MainActionButtonProps {
    text: string;
    onClick: () => void;
    enabled?: boolean;
    loading?: boolean;
    bgColor?: string;
    $fullWidth?: boolean;
    navigation?: 'back' | 'close';
}

export const MainActionButton: React.FC<MainActionButtonProps> = ({
    text,
    onClick,
    enabled = true,
    loading = false,
    bgColor,
    $fullWidth = false,
    navigation,
}) => {
    const label = text || 'Continue';
    const navKind = navigation ?? mainNavKind(label);
    const disabled = !enabled || loading;
    const accent = bgColor || 'var(--tgui--button_color, #3390ec)';
    const disabledColors = getButtonThemeColors(false);
    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick: handleClick } = useLiquidGlassPress({
        disabled,
        onActivate: onClick,
    });

    if (navKind) {
        return (
            <Box
                component={motion.div}
                initial={false}
                animate={{ scale: pressed ? LIQUID_GLASS_CIRCLE_PRESS_SCALE : 1 }}
                transition={liquidGlassPressTransition}
                sx={{
                    flexShrink: 0,
                    width: MINI_BUTTON_ACTION_SIZE,
                    transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
                }}
            >
                <Box
                    sx={{
                        width: MINI_BUTTON_ACTION_SIZE,
                        height: MINI_BUTTON_ACTION_SIZE,
                        borderRadius: '50%',
                        overflow: 'visible',
                        ...liquidGlassCircleShadowSx,
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        aria-label={label}
                        aria-busy={loading || undefined}
                        aria-disabled={disabled || undefined}
                        onClick={handleClick}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerCancel}
                        sx={{
                            width: '100%',
                            height: '100%',
                            p: 0,
                            m: 0,
                            borderRadius: 'inherit',
                            border: 'none',
                            boxSizing: 'border-box',
                            cursor: disabled ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: disabled ? disabledColors.textColor : 'var(--tg-theme-text-color)',
                            WebkitTapHighlightColor: 'transparent',
                            outline: 'none',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            ...liquidGlassSurfaceFillSx,
                            '&:focus-visible': {
                                outline: '2px solid var(--tgui--link_color)',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        {loading
                            ? <CircularProgress size={MINI_BUTTON_ACTION_ICON_SIZE} sx={{ color: 'var(--tg-theme-text-color)' }} />
                            : navKind === 'close'
                              ? <X size={24} weight="bold" />
                              : <ArrowLeft size={24} weight="bold" />}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            component={motion.div}
            initial={false}
            animate={{ scale: pressed ? LIQUID_GLASS_PRESS_SCALE : 1 }}
            transition={liquidGlassPressTransition}
            sx={{
                flexShrink: $fullWidth ? 1 : 0,
                flex: $fullWidth ? '1 1 auto' : undefined,
                width: $fullWidth ? '100%' : undefined,
                height: MINI_BUTTON_ACTION_SIZE,
                transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
            }}
        >
            <Box
                sx={{
                    height: MINI_BUTTON_ACTION_SIZE,
                    width: $fullWidth ? '100%' : 'fit-content',
                    borderRadius: '999px',
                    overflow: 'visible',
                    ...liquidGlassPillShadowSx,
                }}
            >
                <Box
                    component="button"
                    type="button"
                    aria-label={label}
                    aria-busy={loading || undefined}
                    aria-disabled={disabled || undefined}
                    onClick={handleClick}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerCancel}
                    sx={{
                        height: '100%',
                        p: 0,
                        m: 0,
                        px: 2,
                        gap: 0.75,
                        border: 'none',
                        borderRadius: 'inherit',
                        boxSizing: 'border-box',
                        cursor: disabled ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        bgcolor: disabled ? disabledColors.backgroundColor : accent,
                        WebkitTapHighlightColor: 'transparent',
                        outline: 'none',
                        whiteSpace: 'nowrap',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        width: $fullWidth ? '100%' : undefined,
                        '&:focus-visible': {
                            outline: '2px solid var(--tgui--link_color)',
                            outlineOffset: 2,
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: MINI_BUTTON_ACTION_ICON_SIZE,
                            height: MINI_BUTTON_ACTION_ICON_SIZE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: '#fff',
                        }}
                    >
                        {loading
                            ? <CircularProgress size={MINI_BUTTON_ACTION_ICON_SIZE} sx={{ color: '#fff' }} />
                            : mainButtonIcon(label)}
                    </Box>
                    <Text weight="2" style={{ color: '#fff', fontSize: 17, lineHeight: 1 }}>
                        {label}
                    </Text>
                </Box>
            </Box>
        </Box>
    );
};
